import { useState } from "react";
import { Ban, Check, ExternalLink, Mail, Plus, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useActualizeazaTargetarePersoana } from "@/hooks/api/mutate/useActualizeazaTargetarePersoana";
import { useSalveazaTargetarePersoana } from "@/hooks/api/mutate/useSalveazaTargetarePersoana";
import { useStergeTargetarePersoana } from "@/hooks/api/mutate/useStergeTargetarePersoana";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { etichetaSursa } from "@/utils/targetareSurse";
import type { TargetareFunctie, TargetarePersoana } from "@/types/targetare";

type PersoaneFirmaProps = {
  cui: string;
  persoane: TargetarePersoana[];
};

export const ETICHETE_FUNCTIE: Record<TargetareFunctie, string> = {
  director_general: "Director general / CEO",
  director_it: "Director IT / CTO / CIO",
  securitate: "Securitate / CISO",
  dpo: "DPO",
  director_financiar: "Director financiar / CFO",
  administrator: "Administrator",
  conducere: "Conducere",
  reprezentant: "Reprezentant",
  alta: "Altă funcție",
};

const STARE_CLASE: Record<TargetarePersoana["stare"], string> = {
  valid: "bg-ds-green-500 text-black hover:bg-ds-green-500",
  nevalidat: "bg-ds-text-400 text-navy-800 hover:bg-ds-text-400",
  invalid: "bg-ds-amber-500 text-black hover:bg-ds-amber-500",
  opt_out: "bg-destructive text-destructive-foreground hover:bg-destructive",
};

function EditorEmail({
  valoare,
  onSalveaza,
}: {
  valoare: string | null;
  onSalveaza: (email: string) => void;
}) {
  const [text, setText] = useState(valoare ?? "");
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2"
          title="Setează emailul persoanei"
        >
          <Mail className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-2">
        <p className="text-sm font-medium">Email persoană</p>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSalveaza(text.trim());
          }}
          placeholder="prenume.nume@firma.ro"
          className="font-mono text-xs"
          aria-label="Email persoană"
        />
        <Button size="sm" onClick={() => onSalveaza(text.trim())}>
          Salvează
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function RandPersoana({
  persoana,
  ocupat,
  onStare,
  onEmail,
  onSterge,
}: {
  persoana: TargetarePersoana;
  ocupat: boolean;
  onStare: (stare: TargetarePersoana["stare"]) => void;
  onEmail: (email: string) => void;
  onSterge: () => void;
}) {
  const [confirma, setConfirma] = useState(false);
  const verificare = persoana.verificare ?? {};
  const presupus = verificare.presupus === true;
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-md border px-2 py-1.5 text-sm">
      <span className="font-medium">{persoana.nume}</span>
      <Badge variant="secondary" className="font-normal">
        {persoana.functie_text ?? ETICHETE_FUNCTIE[persoana.functie]}
      </Badge>
      {persoana.email && (
        <span className="inline-flex items-center gap-1 font-mono text-xs">
          {persoana.email}
          {persoana.email_sursa && !presupus && (
            <Badge
              variant="outline"
              className="font-normal"
              title="Sursa adresei de email"
            >
              {etichetaSursa(persoana.email_sursa)}
            </Badge>
          )}
          {presupus && (
            <Badge
              variant="outline"
              className="font-normal"
              title="Adresă dedusă din tiparul domeniului; neconfirmată"
            >
              presupus
            </Badge>
          )}
        </span>
      )}
      <Badge className={cn("font-mono", STARE_CLASE[persoana.stare])}>
        {persoana.stare === "opt_out" ? "opt-out" : persoana.stare}
      </Badge>
      <span className="text-xs text-muted-foreground">
        {etichetaSursa(persoana.sursa)}
        {"confirmat_de" in verificare &&
          ` · confirmat de ${etichetaSursa(String(verificare.confirmat_de))}`}
        {persoana.incredere !== null && ` · încredere ${persoana.incredere}%`}
        {"mx" in verificare && ` · MX ${verificare.mx ? "da" : "nu"}`}
        {persoana.localitate && ` · ${persoana.localitate}`}
      </span>
      {persoana.dovada_url && (
        <a
          href={persoana.dovada_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-ds-blue-600 hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          dovadă
        </a>
      )}
      {persoana.linkedin_url && (
        <a
          href={persoana.linkedin_url}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-ds-blue-600 hover:underline"
          title={
            persoana.linkedin_sursa
              ? `Sursă: ${etichetaSursa(persoana.linkedin_sursa)}`
              : undefined
          }
        >
          LinkedIn
          {persoana.linkedin_sursa &&
            ` (${etichetaSursa(persoana.linkedin_sursa)})`}
        </a>
      )}
      {persoana.note && (
        <span className="basis-full text-xs text-muted-foreground">
          {persoana.note}
        </span>
      )}
      <span className="ml-auto flex items-center gap-1">
        <EditorEmail valoare={persoana.email} onSalveaza={onEmail} />
        {persoana.stare !== "valid" && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            disabled={ocupat}
            onClick={() => onStare("valid")}
            title="Confirmă (valid)"
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
        )}
        {persoana.stare !== "invalid" && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            disabled={ocupat}
            onClick={() => onStare("invalid")}
            title="Marchează invalid"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        {persoana.stare !== "opt_out" && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            disabled={ocupat}
            onClick={() => onStare("opt_out")}
            title="Opt-out (nu contactăm persoana)"
          >
            <Ban className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          size="sm"
          variant={confirma ? "destructive" : "ghost"}
          className="h-7 px-2"
          disabled={ocupat}
          onBlur={() => setConfirma(false)}
          onClick={() => {
            if (confirma) onSterge();
            setConfirma(!confirma);
          }}
          title={confirma ? "Apasă din nou pentru a șterge" : "Șterge"}
        >
          <Trash2 className="h-3.5 w-3.5" />
          {confirma && <span className="ml-1 text-xs">Sigur?</span>}
        </Button>
      </span>
    </li>
  );
}

export function PersoaneFirma({ cui, persoane }: PersoaneFirmaProps) {
  const { toast } = useToast();
  const [nume, setNume] = useState("");
  const [functie, setFunctie] = useState<TargetareFunctie>("director_it");
  const [functieText, setFunctieText] = useState("");
  const [email, setEmail] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const salveaza = useSalveazaTargetarePersoana();
  const actualizeaza = useActualizeazaTargetarePersoana();
  const sterge = useStergeTargetarePersoana();
  const ocupat =
    salveaza.isPending || actualizeaza.isPending || sterge.isPending;
  const eroare = (titlu: string) => (e: Error) =>
    toast({ title: titlu, description: e.message, variant: "destructive" });

  const adauga = () => {
    if (!nume.trim()) return;
    salveaza.mutate(
      {
        cui,
        nume: nume.trim(),
        functie,
        functie_text: functieText.trim() || undefined,
        email: email.trim() || undefined,
        linkedin_url: linkedin.trim() || undefined,
      },
      {
        onSuccess: () => {
          setNume("");
          setFunctieText("");
          setEmail("");
          setLinkedin("");
        },
        onError: eroare("Persoana nu a putut fi salvată"),
      }
    );
  };

  return (
    <div className="space-y-3">
      {persoane.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nicio persoană identificată încă.
        </p>
      ) : (
        <ul className="space-y-1">
          {persoane.map((p) => (
            <RandPersoana
              key={p.id}
              persoana={p}
              ocupat={ocupat}
              onStare={(stare) =>
                actualizeaza.mutate(
                  { id: p.id, cui, stare },
                  { onError: eroare("Starea nu a putut fi actualizată") }
                )
              }
              onEmail={(adresa) =>
                actualizeaza.mutate(
                  { id: p.id, cui, email: adresa },
                  { onError: eroare("Emailul nu a putut fi salvat") }
                )
              }
              onSterge={() =>
                sterge.mutate(
                  { id: p.id, cui },
                  { onError: eroare("Persoana nu a putut fi ștearsă") }
                )
              }
            />
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2 rounded-md border border-dashed p-2">
        <Input
          value={nume}
          onChange={(e) => setNume(e.target.value)}
          placeholder="Nume și prenume"
          className="h-8 min-w-[180px] flex-1"
          aria-label="Nume persoană"
        />
        <Select
          value={functie}
          onValueChange={(v) => setFunctie(v as TargetareFunctie)}
        >
          <SelectTrigger className="h-8 w-[210px]" aria-label="Funcție">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(ETICHETE_FUNCTIE) as TargetareFunctie[]).map((f) => (
              <SelectItem key={f} value={f}>
                {ETICHETE_FUNCTIE[f]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={functieText}
          onChange={(e) => setFunctieText(e.target.value)}
          placeholder="funcția exactă (opțional)"
          className="h-8 min-w-[160px] flex-1"
          aria-label="Funcție exactă"
        />
        <Input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email (opțional)"
          className="h-8 min-w-[180px] flex-1 font-mono text-xs"
          aria-label="Email persoană nouă"
        />
        <Input
          value={linkedin}
          onChange={(e) => setLinkedin(e.target.value)}
          placeholder="LinkedIn (opțional)"
          className="h-8 min-w-[160px] flex-1 font-mono text-xs"
          aria-label="LinkedIn persoană nouă"
        />
        <Button
          size="sm"
          className="h-8"
          disabled={ocupat || !nume.trim()}
          onClick={adauga}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Adaugă
        </Button>
      </div>
    </div>
  );
}
